import { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FeePlanDialog, FREQ_LABEL } from "@/components/FeePlanDialog";
import { FeeLedgerDialog, inr, fdate } from "@/components/FeeLedgerDialog";
import { RequestsPanel } from "@/components/FeeRequestsPanel";
import { FeeReminderCard } from "@/components/FeeReminderCard";
import { UnpaidPanel } from "@/components/FeeUnpaidPanel";
import { ClassFeesPanel } from "@/components/ClassFeesPanel";
import { exportPdf, exportExcel } from "@/lib/feeExport";
import { Plus, Wallet, TrendingUp, Clock, CalendarDays, CalendarRange, AlertTriangle, FileDown, FileSpreadsheet, Pencil } from "lucide-react";

const ALL = "__all__";

function Widgets({ w }) {
  const items = [["Total fees", w.total_fees, Wallet, "bg-violet-50 text-violet-700"], ["Total collected", w.total_collected, TrendingUp, "bg-emerald-50 text-emerald-700"],
    ["Pending collection", w.pending_collection, Clock, "bg-amber-50 text-amber-700"], ["Today's collection", w.today_collection, CalendarDays, "bg-blue-50 text-blue-700"],
    ["This month", w.month_collection, CalendarRange, "bg-cyan-50 text-cyan-700"], ["Overdue fees", w.overdue_fees, AlertTriangle, "bg-rose-50 text-rose-700"]];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
      {items.map(([l, v, Icon, c]) => (
        <div key={l} data-testid={`fee-widget-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="bg-white rounded-2xl p-4 shadow-sm flex items-center gap-3">
          <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${c}`}><Icon size={20} /></div>
          <div><div className="text-xs text-slate-500">{l}</div><div className="font-display text-xl font-extrabold text-slate-900">{inr(v)}</div></div>
        </div>
      ))}
      <div className="col-span-2 lg:col-span-3 text-xs text-slate-500">{w.plans || 0} fee plans • {w.overdue_students || 0} students overdue • {w.open_requests || 0} payment requests waiting</div>
    </div>
  );
}

const REPORTS = {
  collection: { title: "Fee collection report", cols: [["receipt_no", "Receipt"], ["date", "Date"], ["student_name", "Student"], ["class_name", "Class"], ["section", "Sec"], ["installment_no", "Inst"], ["mode", "Mode"], ["transaction_id", "Txn ID"], ["amount", "Amount", true]] },
  "class-wise": { title: "Class-wise fee report", cols: [["class_name", "Class"], ["section", "Section"], ["students", "Students"], ["total", "Total fee", true], ["collected", "Collected", true], ["pending", "Pending", true], ["overdue", "Overdue", true]] },
  defaulters: { title: "Defaulter report", cols: [["student_name", "Student"], ["admission_number", "Adm No"], ["class_name", "Class"], ["section", "Sec"], ["installments", "Overdue inst."], ["oldest_due", "Oldest due"], ["overdue", "Overdue", true], ["late_fee", "Late fee", true], ["parent", "Parent"], ["phone", "Phone"]] },
};

function ReportsPanel({ branchId }) {
  const [kind, setKind] = useState("collection");
  const month = new Date().toISOString().slice(0, 8);
  const [from, setFrom] = useState(`${month}01`);
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState([]);
  const def = REPORTS[kind];
  const cols = def.cols.map(([key, label, money]) => ({ key, label, money }));
  useEffect(() => {
    const params = { ...(branchId ? { branch_id: branchId } : {}), ...(kind === "collection" ? { date_from: from, date_to: to } : {}) };
    api.get(`/fee-mgmt/reports/${kind}`, { params }).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [kind, from, to, branchId]);
  const total = (k) => rows.reduce((a, b) => a + (Number(b[k]) || 0), 0);
  const subtitle = kind === "collection" ? `${fdate(from)} to ${fdate(to)}` : "";

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger data-testid="report-kind" className="w-56 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(REPORTS).map(([k, v]) => <SelectItem key={k} value={k}>{v.title}</SelectItem>)}</SelectContent>
        </Select>
        {kind === "collection" && <>
          <div><Label className="text-xs">From</Label><Input data-testid="report-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="bg-white mt-1" /></div>
          <div><Label className="text-xs">To</Label><Input data-testid="report-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="bg-white mt-1" /></div>
        </>}
        <div className="ml-auto flex gap-2">
          <Button data-testid="report-pdf" variant="outline" onClick={() => exportPdf(def.title, cols, rows, subtitle)} disabled={!rows.length}><FileDown size={15} className="mr-1" /> PDF</Button>
          <Button data-testid="report-excel" variant="outline" onClick={() => exportExcel(def.title, cols, rows)} disabled={!rows.length}><FileSpreadsheet size={15} className="mr-1" /> Excel</Button>
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-violet-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-violet-50 text-slate-700"><tr>{cols.map((c) => <th key={c.key} className={`px-3 py-2.5 ${c.money ? "text-right" : "text-left"}`}>{c.label}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && <tr><td colSpan={cols.length} className="text-center py-8 text-slate-500">No data</td></tr>}
            {rows.map((r, i) => <tr key={i}>{cols.map((c) => <td key={c.key} className={`px-3 py-2 ${c.money ? "text-right font-medium" : ""}`}>{c.money ? inr(r[c.key]) : r[c.key]}</td>)}</tr>)}
          </tbody>
          {rows.length > 0 && <tfoot className="bg-violet-50 font-semibold"><tr>{cols.map((c, i) => <td key={c.key} className={`px-3 py-2 ${c.money ? "text-right" : ""}`}>{c.money ? inr(total(c.key)) : i === 0 ? `Total (${rows.length})` : ""}</td>)}</tr></tfoot>}
        </table>
      </div>
    </div>
  );
}

export default function FeeManagementPage() {
  const { user } = useAuth();
  const isGlobal = ["master_admin", "super_admin"].includes(user?.role);
  const canManage = isGlobal || user?.role === "accountant";
  const [branches, setBranches] = useState([]);
  const [types, setTypes] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [schoolType, setSchoolType] = useState(ALL);
  const [widgets, setWidgets] = useState({});
  const [plans, setPlans] = useState([]);
  const [students, setStudents] = useState([]);
  const [planOpen, setPlanOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [statusF, setStatusF] = useState(ALL);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    Promise.all([api.get("/branches"), api.get("/meta/school-types")]).then(([b, t]) => { setBranches(b.data); setTypes(t.data); if (!isGlobal) setBranchId(user?.branch_id || ""); }).catch(() => {});
  }, [isGlobal, user]);

  const bp = branchId ? { branch_id: branchId } : {};
  function loadAll() {
    setRefreshKey((k) => k + 1);
    api.get("/fee-mgmt/dashboard", { params: bp }).then((r) => setWidgets(r.data)).catch(() => {});
    api.get("/fee-mgmt/plans", { params: { ...bp, ...(schoolType !== ALL ? { school_type: schoolType } : {}) } }).then((r) => setPlans(r.data)).catch(() => {});
    api.get("/students", { params: bp }).then((r) => setStudents(r.data)).catch(() => {});
  }
  useEffect(() => { loadAll(); }, [branchId, schoolType]); // eslint-disable-line react-hooks/exhaustive-deps

  const branchOpts = branches.filter((b) => schoolType === ALL || b.school_type === schoolType);
  const shown = plans.filter((p) => statusF === ALL || p.status === statusF);

  return (
    <AppShell>
      <PageHeader eyebrow="Finance" title="Fee Management" subtitle="Student fee plans, installments, parent payment requests and reports.">
        {isGlobal && (
          <>
            <Select value={schoolType} onValueChange={(v) => { setSchoolType(v); setBranchId(""); }}>
              <SelectTrigger data-testid="fm-school-type" className="w-44 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={ALL}>All school types</SelectItem>{types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={branchId || ALL} onValueChange={(v) => setBranchId(v === ALL ? "" : v)}>
              <SelectTrigger data-testid="fm-branch" className="w-52 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={ALL}>All branches</SelectItem>{branchOpts.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
          </>
        )}
      </PageHeader>
      <Tabs defaultValue="dashboard">
        <TabsList className="bg-violet-100 flex-wrap h-auto">
          <TabsTrigger data-testid="fm-tab-dashboard" value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger data-testid="fm-tab-plans" value="plans">Student Fees</TabsTrigger>
          <TabsTrigger data-testid="fm-tab-class-fees" value="class-fees">Class Fees</TabsTrigger>
          <TabsTrigger data-testid="fm-tab-requests" value="requests">Payment Requests</TabsTrigger>
          <TabsTrigger data-testid="fm-tab-reports" value="reports">Reports</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard" className="mt-5 space-y-4"><Widgets w={widgets} /><FeeReminderCard canSend={canManage} /><UnpaidPanel branchId={branchId} canManage={canManage} onManual={setLedger} refreshKey={refreshKey} /></TabsContent>
        <TabsContent value="plans" className="mt-5 space-y-3">
          <div className="flex flex-wrap gap-2 justify-between">
            <Select value={statusF} onValueChange={setStatusF}>
              <SelectTrigger data-testid="fm-plan-status" className="w-44 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={ALL}>All statuses</SelectItem><SelectItem value="on_track">On track</SelectItem><SelectItem value="overdue">Overdue</SelectItem><SelectItem value="paid">Fully paid</SelectItem></SelectContent>
            </Select>
            {canManage && <Button data-testid="fm-new-plan" onClick={() => { setEditing(null); setPlanOpen(true); }} className="bg-violet-600 hover:bg-violet-700"><Plus size={16} className="mr-1" /> Assign fee plan</Button>}
          </div>
          <div className="bg-white rounded-2xl border border-violet-100 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-violet-50 text-slate-700"><tr><th className="text-left px-3 py-2.5">Student</th><th className="text-left px-3">Plan</th><th className="text-right px-3">Total</th><th className="text-right px-3">Paid</th><th className="text-right px-3">Pending</th><th className="text-right px-3">Late fee</th><th className="px-3">Next due</th><th className="px-3">Status</th><th /></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {shown.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-slate-500">No fee plans yet.</td></tr>}
                {shown.map((p) => (
                  <tr key={p.id} data-testid={`fm-plan-${p.id}`} className="hover:bg-violet-50/40 cursor-pointer" onClick={() => setLedger(p.id)}>
                    <td className="px-3 py-2.5"><div className="font-semibold text-slate-900">{p.student_name}</div><div className="text-xs text-slate-500">{p.admission_number} • {p.class_name} {p.section}</div></td>
                    <td className="px-3 text-xs">{p.academic_year}<br />{FREQ_LABEL[p.frequency]}{p.discount_pct > 0 && <><br /><span className="text-rose-600">−{p.discount_pct}%</span></>}</td>
                    <td className="text-right px-3">{inr(p.net_total)}</td><td className="text-right px-3 text-emerald-700">{inr(p.paid)}</td>
                    <td className="text-right px-3 text-rose-700 font-semibold">{inr(p.pending)}</td><td className="text-right px-3 text-orange-700">{inr(p.late_fee_due)}</td>
                    <td className="px-3 text-center text-xs">{fdate(p.next_due_date)}</td>
                    <td className="px-3 text-center"><Badge className={p.status === "paid" ? "bg-emerald-100 text-emerald-800" : p.status === "overdue" ? "bg-rose-100 text-rose-800" : "bg-blue-100 text-blue-800"}>{p.status.replace("_", " ")}</Badge></td>
                    <td className="px-3">{canManage && p.paid === 0 && <Button data-testid={`fm-edit-${p.id}`} size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setEditing(p); setPlanOpen(true); }}><Pencil size={14} /></Button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
        <TabsContent value="class-fees" className="mt-5"><ClassFeesPanel branchId={branchId} branches={branches} canManage={canManage} onChanged={loadAll} /></TabsContent>
        <TabsContent value="requests" className="mt-5"><RequestsPanel branchId={branchId} /></TabsContent>
        <TabsContent value="reports" className="mt-5"><ReportsPanel branchId={branchId} /></TabsContent>
      </Tabs>
      <FeePlanDialog open={planOpen} onClose={() => setPlanOpen(false)} students={students} editing={editing} onSaved={loadAll} />
      <FeeLedgerDialog planId={ledger} onClose={() => setLedger(null)} canManage={canManage} onChanged={loadAll} />
    </AppShell>
  );
}
